import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useWorkspace } from "@/app/WorkspaceContext";
import { usePages } from "@/data/hooks";
import { builder, type ComponentTree, type Page, type PageVersion } from "@/data/services";
import { streamChat } from "@/data/chat";
import { loadChatHistory, saveChatHistory, clearChatHistory } from "./chatHistory";
import { getRegistrySpec } from "@/editor/registrySpec";
import { addChildNode, applyOps, cloneSubtree, duplicateNode, findParent, moveChild, newNodeId, removeNode as removeNodeOp, setNodeProp } from "@/editor/treeOps";
import { findNode } from "@/editor/findNode";
import { validateOp } from "@/editor/validateProposalOp";
import type { ComponentNode, Proposal, ProposalOp } from "@/editor/types";
import { registry, type RegistryName } from "@dynamic-landing/components";

/**
 * The live AI page builder. Active only when the workspace is backed by real
 * backend data AND the current project has a real page. It:
 *
 *  1. loads the page's current version tree (getPage → current_version.tree, or
 *     getVersion as a fallback) into a working ComponentTree held in state;
 *  2. builds the registry_spec the agent needs to stay inside the design system;
 *  3. send(prompt) streams from POST /ai/v1/chat (SSE), accumulating Nara's
 *     reply text and collecting tree_patch proposals from `proposal` events;
 *  4. applyProposal(p) validates each op, then applies the valid subset to the
 *     working tree (pure, immutable) so the preview updates instantly;
 *  5. save() PATCHes the working tree onto the version; publish() saves first,
 *     then promotes the version to prod.
 *
 * When not live (offline / no page), `live` is false and MakeScreen keeps the
 * polished simulated flow.
 */
export interface BuilderProposal extends Proposal {
  /** Monotonic per-conversation id so React keys are stable across re-renders. */
  key: string;
  /** Set once the proposal's valid ops have been applied to the working tree. */
  applied?: boolean;
}

/** One committed turn in the Make conversation thread (plain text). Proposals
 *  for the latest turn render from the live `proposals` array. */
export interface ChatMessage {
  role: "user" | "assistant";
  content: string;
  key: string;
}

export interface UseBuilder {
  /** true when a real page is loaded and the AI builder is wired. */
  live: boolean;
  /** The working component tree (mutated locally by applyProposal). */
  tree: ComponentTree | null;
  /** Nara's accumulated streamed reply text. */
  reply: string;
  /** true while an SSE stream is in flight. */
  streaming: boolean;
  /** Proposals collected from the current/last conversation, newest last. */
  proposals: BuilderProposal[];
  /** The committed conversation thread (user + Nara turns), oldest first. */
  messages: ChatMessage[];
  /** Send a prompt to the agent over SSE. */
  send: (prompt: string) => void;
  /** Validate + apply a proposal's ops to the working tree. */
  applyProposal: (p: BuilderProposal) => void;
  /**
   * Apply a PURE tree transform (e.g. a layout recipe) to the working tree.
   * Snapshots the current tree onto the SAME undo stack manual edits use, sets
   * the result as the working tree, marks it dirty (via baseline diff) and
   * clears selection — so ⌘Z / the Undo button revert it like any edit. A
   * transform that returns the input unchanged is a no-op (no undo entry).
   */
  applyTreeTransform: (fn: (t: ComponentTree) => ComponentTree, label?: string) => void;
  /** PATCH the working tree onto the page's version. */
  save: () => Promise<void>;
  /** Save, then publish the version to prod. */
  publish: () => Promise<void>;
  /** Reset the conversation (reply + proposals); keeps the loaded tree. */
  reset: () => void;
  /** The loaded page (for domain / titles), or undefined while loading. */
  page: Page | undefined;

  /* ---- which page is being edited (page context) ---- */
  /** All pages in the current project (for the page picker). */
  pages: Page[];
  /** The id of the page Make is currently editing. */
  activePageId: string | undefined;
  /** The active page resolved from the list (title / slug / status), if loaded. */
  activePage: Page | undefined;
  /** Switch which page Make edits. Resets the conversation for the new page. */
  setActivePage: (id: string) => void;
  /** Re-fetch the current version's tree from the server (e.g. after a restore),
   *  discarding local edits/undo for the page. */
  reloadTree: () => void;

  /** true while the initial tree is loading. */
  loadingTree: boolean;
  /** non-null when the last save/publish failed. */
  error: string | null;
  /** true while a save/publish request is in flight. */
  saving: boolean;
  /** true when the working tree differs from what was last loaded/saved. */
  dirty: boolean;

  /* ---- manual editing (AI-first hybrid) ---- */
  /** Currently-selected node id in the preview, or null. */
  selectedNodeId: string | null;
  /** The selected node, resolved from the working tree. */
  selectedNode: ComponentNode | null;
  /** Select a node (click-to-select on the preview). */
  select: (id: string | null) => void;
  /** Edit a prop on a node by hand; pushes undo history. */
  setProp: (nodeId: string, prop: string, value: unknown) => void;
  /** Add a block; parent = selected container, else page root. Selects it. */
  addBlock: (componentName: string) => void;
  /** Move a node up (-1) or down (+1) among its siblings. */
  moveBlock: (nodeId: string, delta: -1 | 1) => void;
  /** Remove a node (cannot remove the root). */
  removeBlock: (nodeId: string) => void;
  /** Duplicate a node as the next sibling (fresh ids); selects the clone. */
  duplicateBlock: (nodeId: string) => void;
  /** Copy a node's subtree to the in-editor clipboard. */
  copyBlock: (nodeId: string) => void;
  /** Paste the clipboard as a child of the selected container (else root). */
  pasteBlock: () => void;
  /** Whether the clipboard holds a block (drives ⌘V availability). */
  canPaste: boolean;
  undo: () => void;
  redo: () => void;
  canUndo: boolean;
  canRedo: boolean;
}

type RegEntry = { version: string; acceptsChildren: boolean };
const reg = registry as Record<string, RegEntry | undefined>;
/** Component names that accept children (for the Add-block menu target logic). */
export const COMPONENT_NAMES = Object.keys(registry) as RegistryName[];

/** Per-op validation outcome bundled with the op, for the proposal card UI. */
export type OpRow = {
  op: ProposalOp;
  ok: boolean;
  reason?: string;
};

/** Validate every op in a proposal against a tree (UI helper). */
export function reviewProposal(tree: ComponentTree | null, p: BuilderProposal): OpRow[] {
  const ops = p.ops ?? [];
  if (!tree) return ops.map((op) => ({ op, ok: false, reason: "no page tree loaded" }));
  return ops.map((op) => {
    const v = validateOp(tree, op);
    return v.ok ? { op, ok: true } : { op, ok: false, reason: v.reason };
  });
}

export function useBuilder(initialPageId?: string): UseBuilder {
  const { live: wsLive, currentProjectId } = useWorkspace();
  const pages = usePages(currentProjectId);

  // ---- which page is being edited (the active page) ----
  // CONTRACT: an incoming `initialPageId` (react-router location.state.pageId,
  // read in MakeScreen) seeds the initial active page; a later Library workflow
  // relies on this. Otherwise default to the first page.
  // Remember the last page edited per project, so Make resumes where you left
  // off instead of resetting to the first page (or feeling like a new page) each visit.
  const lastKey = currentProjectId ? `make.activePage.${currentProjectId}` : undefined;
  const [activePageId, setActivePageId] = useState<string | undefined>(initialPageId);
  // Seed / repair the active page once the list loads: honor a valid
  // initialPageId, keep a still-valid current selection, else the last-edited
  // page (persisted), else fall back to [0].
  useEffect(() => {
    if (!pages.data) return;
    const valid = (id: string | undefined | null) => !!id && pages.data!.some((p) => p.id === id);
    setActivePageId((cur) => {
      if (valid(cur)) return cur;
      if (valid(initialPageId)) return initialPageId;
      const stored = lastKey ? localStorage.getItem(lastKey) : null;
      if (valid(stored)) return stored!;
      return pages.data![0]?.id;
    });
  }, [pages.data, initialPageId, lastKey]);

  const pageId = activePageId;
  const canLive = wsLive && !!pageId;
  const activePage = useMemo(
    () => pages.data?.find((p) => p.id === pageId),
    [pages.data, pageId],
  );
  const setActivePage = useCallback(
    (id: string) => {
      setActivePageId(id);
      if (lastKey) localStorage.setItem(lastKey, id);
    },
    [lastKey],
  );

  // ---- working tree + page/version identity ----
  const [tree, setTree] = useState<ComponentTree | null>(null);
  const [page, setPage] = useState<Page | undefined>(undefined);
  const [versionId, setVersionId] = useState<string | undefined>(undefined);
  const [loadingTree, setLoadingTree] = useState(false);
  // A serialized snapshot of the tree as last loaded/saved, for dirty detection.
  const baselineRef = useRef<string | null>(null);

  // registry_spec — memoized via the editor, computed once here.
  const registrySpec = useMemo(() => getRegistrySpec(), []);

  // ---- conversation state ----
  const [reply, setReply] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [proposals, setProposals] = useState<BuilderProposal[]>([]);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const abort = useRef<AbortController | null>(null);
  const propSeq = useRef(0);
  const msgSeq = useRef(0);

  // ---- save/publish state ----
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ---- manual-editing state (selection + undo/redo) ----
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [undoStack, setUndoStack] = useState<ComponentTree[]>([]);
  const [redoStack, setRedoStack] = useState<ComponentTree[]>([]);
  // In-editor clipboard for copy/paste of a block subtree (cloned on paste).
  const clipboard = useRef<ComponentNode | null>(null);
  const [canPaste, setCanPaste] = useState(false);
  // Bumped to force a tree re-fetch (e.g. after restoring an old version).
  const [reloadNonce, setReloadNonce] = useState(0);
  const reloadTree = useCallback(() => setReloadNonce((n) => n + 1), []);

  // Load the current version tree whenever the live page changes.
  useEffect(() => {
    // Any (re)load discards manual-edit history + selection.
    setSelectedNodeId(null);
    setUndoStack([]);
    setRedoStack([]);
    if (!canLive || !pageId) {
      setTree(null);
      setPage(undefined);
      setVersionId(undefined);
      baselineRef.current = null;
      return;
    }
    let cancelled = false;
    setLoadingTree(true);
    setError(null);
    (async () => {
      try {
        const p = await builder.getPage(pageId);
        if (cancelled) return;
        setPage(p);
        const vId = p.current_version_id;
        setVersionId(vId);
        let loaded: ComponentTree | undefined = p.current_version?.tree;
        if (!loaded && vId) {
          const v: PageVersion = await builder.getVersion(pageId, vId);
          if (cancelled) return;
          loaded = v.tree;
        }
        if (cancelled) return;
        if (loaded) {
          setTree(loaded);
          baselineRef.current = JSON.stringify(loaded);
        } else {
          setTree(null);
          baselineRef.current = null;
        }
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : "Failed to load page");
          setTree(null);
        }
      } finally {
        if (!cancelled) setLoadingTree(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canLive, pageId, reloadNonce]);

  // Load the active page's persisted conversation whenever the page changes
  // (including the first assignment) and drop transient stream state — so one
  // page's reply/proposals never bleed into another AND the thread survives both
  // page switches and a full reload. Re-key the loaded turns with fresh
  // sequential ids so new sends can't collide with keys from a prior session.
  useEffect(() => {
    abort.current?.abort();
    setReply("");
    setProposals([]);
    setStreaming(false);
    const loaded = loadChatHistory(pageId);
    let n = 0;
    const rekeyed = loaded.map((m) => ({ ...m, key: `${m.role === "user" ? "u" : "a"}${++n}` }));
    msgSeq.current = n;
    setMessages(rekeyed);
  }, [pageId]);

  // Cancel any in-flight stream on unmount.
  useEffect(() => () => abort.current?.abort(), []);

  const send = useCallback(
    (prompt: string) => {
      if (!canLive || !pageId) return;
      abort.current?.abort();
      const ac = new AbortController();
      abort.current = ac;
      // Thread the prior turns so Nara has context, THEN append this user turn.
      const history = messages.map((m) => ({ role: m.role, content: m.content }));
      msgSeq.current += 1;
      const userMsg: ChatMessage = { role: "user", content: prompt, key: `u${msgSeq.current}` };
      setMessages((prev) => {
        const next = [...prev, userMsg];
        saveChatHistory(pageId, next); // persist per page (idempotent; safe under StrictMode)
        return next;
      });
      setReply("");
      setProposals([]);
      setStreaming(true);
      setError(null);

      let acc = "";
      const pushText = (s: string) => {
        acc += (acc ? "\n\n" : "") + s;
        setReply(acc);
      };

      streamChat(
        {
          page_id: pageId,
          ...(versionId ? { version_id: versionId } : {}),
          message: prompt,
          registry_spec: registrySpec,
          history,
        },
        (e) => {
          if (e.type === "thinking") {
            if (typeof e.data === "string" && e.data.trim()) pushText(e.data);
          } else if (e.type === "proposal") {
            const raw = e.data as Partial<Proposal> | null;
            if (!raw) return;
            const ops = Array.isArray(raw.ops) ? (raw.ops as ProposalOp[]) : [];
            // Only tree_patch proposals carry ops we can apply to the tree.
            if (raw.kind && raw.kind !== "tree_patch") {
              if (raw.rationale) pushText(raw.rationale);
              return;
            }
            propSeq.current += 1;
            const built: BuilderProposal = {
              id: raw.id ?? `prop_${propSeq.current}`,
              key: `prop_${propSeq.current}`,
              kind: "tree_patch",
              rationale: raw.rationale ?? "Proposed changes",
              ops,
            };
            setProposals((prev) => [...prev, built]);
          } else if (e.type === "error") {
            pushText("(Nara hit an error.)");
          }
        },
        ac.signal,
      )
        .catch((err) => {
          if ((err as Error)?.name !== "AbortError") {
            pushText("(Couldn't reach Nara just now.)");
          }
        })
        .finally(() => {
          setStreaming(false);
          // Commit Nara's reply into the thread (plain text; proposals stay in
          // `proposals` for the latest turn), then clear the streaming bubble.
          const text = acc.trim();
          if (text) {
            msgSeq.current += 1;
            const aMsg: ChatMessage = { role: "assistant", content: text, key: `a${msgSeq.current}` };
            setMessages((prev) => {
              const next = [...prev, aMsg];
              // pageId is captured at send() time, so the reply persists to the
              // page it was about even if the user has since switched pages.
              saveChatHistory(pageId, next);
              return next;
            });
          }
          setReply("");
        });
    },
    [canLive, pageId, versionId, registrySpec, messages],
  );

  // Single mutation entry point: snapshot current tree to undo, clear redo.
  const commit = useCallback(
    (next: ComponentTree | null) => {
      if (!next || !tree || next === tree) return;
      setUndoStack((s) => [...s.slice(-49), tree]);
      setRedoStack([]);
      setTree(next);
    },
    [tree],
  );

  const select = useCallback((id: string | null) => setSelectedNodeId(id), []);

  const selectedNode = useMemo(
    () => (selectedNodeId && tree ? findNode(tree.root, selectedNodeId) : null),
    [selectedNodeId, tree],
  );

  const applyProposal = useCallback(
    (p: BuilderProposal) => {
      if (!tree) return;
      const ops = p.ops ?? [];
      const valid = ops.filter((op) => validateOp(tree, op).ok);
      if (valid.length > 0) commit(applyOps(tree, valid));
      setProposals((prev) => prev.map((x) => (x.key === p.key ? { ...x, applied: true } : x)));
    },
    [tree, commit],
  );

  const setProp = useCallback(
    (nodeId: string, prop: string, value: unknown) => {
      if (!tree) return;
      commit(setNodeProp(tree, nodeId, prop, value));
    },
    [tree, commit],
  );

  // Apply a whole-tree transform (layout recipes) through the same undoable
  // `commit` path as manual edits. The transform is pure; if it throws or
  // returns the same reference, this is a safe no-op. `label` is advisory.
  const applyTreeTransform = useCallback(
    (fn: (t: ComponentTree) => ComponentTree, _label?: string) => {
      if (!tree) return;
      let next: ComponentTree;
      try {
        next = fn(tree);
      } catch {
        return;
      }
      if (!next || next === tree) return;
      commit(next); // pushes current tree to undo, clears redo, sets working tree
      setSelectedNodeId(null);
    },
    [tree, commit],
  );

  const addBlock = useCallback(
    (componentName: string) => {
      if (!tree) return;
      const entry = reg[componentName];
      if (!entry) return;
      // Sensible parent: the selected container, else the page root.
      let parentId = tree.root.node_id;
      if (selectedNodeId) {
        const n = findNode(tree.root, selectedNodeId);
        if (n && reg[n.component.name]?.acceptsChildren) parentId = n.node_id;
      }
      const node: ComponentNode = {
        node_id: newNodeId(componentName),
        component: { name: componentName, version: entry.version },
        props: {},
      };
      commit(addChildNode(tree, parentId, node));
      setSelectedNodeId(node.node_id);
    },
    [tree, selectedNodeId, commit],
  );

  const moveBlock = useCallback(
    (nodeId: string, delta: -1 | 1) => {
      if (!tree) return;
      commit(moveChild(tree, nodeId, delta));
    },
    [tree, commit],
  );

  const removeBlock = useCallback(
    (nodeId: string) => {
      if (!tree) return;
      commit(removeNodeOp(tree, nodeId));
      setSelectedNodeId((cur) => (cur === nodeId ? null : cur));
    },
    [tree, commit],
  );

  const duplicateBlock = useCallback(
    (nodeId: string) => {
      if (!tree) return;
      const res = duplicateNode(tree, nodeId);
      if (!res) return;
      commit(res.tree);
      setSelectedNodeId(res.newId);
    },
    [tree, commit],
  );

  const copyBlock = useCallback(
    (nodeId: string) => {
      if (!tree || nodeId === tree.root.node_id) return;
      const n = findNode(tree.root, nodeId);
      if (n) {
        clipboard.current = n;
        setCanPaste(true);
      }
    },
    [tree],
  );

  const pasteBlock = useCallback(() => {
    if (!tree || !clipboard.current) return;
    const clone = cloneSubtree(clipboard.current);
    // Target: the selected container if it accepts children, else the selected
    // node's parent, else the page root.
    let parentId = tree.root.node_id;
    if (selectedNodeId) {
      const sel = findNode(tree.root, selectedNodeId);
      if (sel && reg[sel.component.name]?.acceptsChildren) {
        parentId = sel.node_id;
      } else {
        const par = findParent(tree, selectedNodeId);
        if (par) parentId = par.node_id;
      }
    }
    commit(addChildNode(tree, parentId, clone));
    setSelectedNodeId(clone.node_id);
  }, [tree, selectedNodeId, commit]);

  const undo = useCallback(() => {
    if (!undoStack.length || !tree) return;
    const prev = undoStack[undoStack.length - 1]!;
    setRedoStack((r) => [...r.slice(-49), tree]);
    setUndoStack((s) => s.slice(0, -1));
    setTree(prev);
  }, [undoStack, tree]);

  const redo = useCallback(() => {
    if (!redoStack.length || !tree) return;
    const next = redoStack[redoStack.length - 1]!;
    setUndoStack((u) => [...u.slice(-49), tree]);
    setRedoStack((s) => s.slice(0, -1));
    setTree(next);
  }, [redoStack, tree]);

  const save = useCallback(async () => {
    if (!pageId || !versionId || !tree) {
      setError("Nothing to save yet.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await builder.saveVersion(pageId, versionId, tree, "Edited in Make");
      baselineRef.current = JSON.stringify(tree);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Save failed");
      throw e;
    } finally {
      setSaving(false);
    }
  }, [pageId, versionId, tree]);

  const publish = useCallback(async () => {
    if (!pageId || !versionId || !tree) {
      setError("Nothing to publish yet.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await builder.saveVersion(pageId, versionId, tree, "Edited in Make");
      baselineRef.current = JSON.stringify(tree);
      await builder.publish(pageId, versionId, "prod");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Publish failed");
      throw e;
    } finally {
      setSaving(false);
    }
  }, [pageId, versionId, tree]);

  const reset = useCallback(() => {
    abort.current?.abort();
    setReply("");
    setProposals([]);
    setMessages([]);
    setStreaming(false);
    clearChatHistory(pageId); // "Start over" wipes the saved thread for this page
  }, [pageId]);

  const dirty = useMemo(() => {
    if (!tree || baselineRef.current == null) return false;
    return JSON.stringify(tree) !== baselineRef.current;
  }, [tree]);

  return {
    live: canLive,
    tree,
    reply,
    streaming,
    proposals,
    messages,
    send,
    applyProposal,
    applyTreeTransform,
    save,
    publish,
    reset,
    page,
    pages: pages.data ?? [],
    activePageId,
    activePage,
    setActivePage,
    reloadTree,
    loadingTree,
    error,
    saving,
    dirty,
    selectedNodeId,
    selectedNode,
    select,
    setProp,
    addBlock,
    moveBlock,
    removeBlock,
    duplicateBlock,
    copyBlock,
    pasteBlock,
    canPaste,
    undo,
    redo,
    canUndo: undoStack.length > 0,
    canRedo: redoStack.length > 0,
  };
}
